import { Flex } from '@mantine/core';
import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, ITargetable, Targeting } from '@feathr/blackbox';
import { Button, CardV2 as Card, EmptyState, Radios, Text } from '@feathr/components';
import { cssVar, getIconForAction } from '@feathr/hooks';
import { useFlags } from '@feathr/report_components';

import { useLocationFilters } from '../Targetings.useLocationFilters';
import GeoFilter from './GeoFilter';
import GeoFilterModal from './GeoFilterModal';
import GeoFilterSet from './GeoFilterSet';
import { useModals } from './Locations.useModals';
import SaveCustomSetModal from './SaveCustomSetModal';

enum GeoFilterMode {
  NONE = 'none',
  SAVED_SET = 'saved_set',
  CUSTOM_SET = 'custom_set',
}

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function Locations({ campaign, targetings }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { isOmniCampaign } = campaign;
  const [geoFilterMode, setGeoFilterMode] = useState<GeoFilterMode>(GeoFilterMode.NONE);
  const { hasSavedGeoFilterSets } = useFlags();

  const {
    filteredTargetings,
    remainingLocations,
    canAddMore,
    handleAddFilter,
    handleRemoveFilter,
  } = useLocationFilters({ campaign, targetings });

  const { editCreate, saveCustomSet } = useModals();

  const handleSaveCustomSet = (): void => {
    saveCustomSet.open();
  };

  const handleSaveCustomSetSubmit = (name: string): void => {
    // TODO: Implement actual save logic with the name
    // eslint-disable-next-line no-console
    console.log('Save custom set with name:', name);
  };

  const geofilterOptions = [
    {
      id: GeoFilterMode.NONE,
      name: t("Don't use geofilters"),
    },
    {
      id: GeoFilterMode.SAVED_SET,
      name: t('Use a saved set'),
    },
    {
      id: GeoFilterMode.CUSTOM_SET,
      name: t('Use custom geofilters'),
    },
  ];

  const addFilterButton = (
    <Flex align={'center'} gap={cssVar('--spacing-4')} key={'add-filter-section'}>
      <Button
        disabled={!canAddMore}
        id={'addFilter'}
        onClick={handleAddFilter}
        prefix={getIconForAction('add')}
      >
        {t('Add filter')}
      </Button>
      {hasSavedGeoFilterSets && filteredTargetings.length > 0 && (
        <Text.DefaultRegular>
          {t('{{count}} locations remaining', { count: remainingLocations })}
        </Text.DefaultRegular>
      )}
    </Flex>
  );

  const emptyState = (
    <EmptyState
      description={t('Add a filter to get started')}
      label={t('No filters added')}
      theme={'slate'}
    >
      {addFilterButton}
    </EmptyState>
  );

  const handleEditGeoFilterSet = (geoFilterSet: ITargetable): void => {
    // TODO: Implement edit modal integration
    // eslint-disable-next-line no-console
    console.log(geoFilterSet);
  };

  return (
    <Card width={isOmniCampaign ? 'full' : 'narrow'}>
      <Card.Header
        description={t(
          'Use Filters to include or exclude locations. Ads will only show to people in included areas and will not show to people in excluded areas. Filtering is optional but recommended.',
        )}
        isCollapsible={true}
        title={t('Filters')}
      />
      <Card.Content addVerticalGap={true}>
        {hasSavedGeoFilterSets ? (
          <>
            <Radios
              dataName={'geofilter-mode-selection'}
              layout={'block'}
              onChange={(newValue): void => setGeoFilterMode(newValue as GeoFilterMode)}
              options={geofilterOptions}
              orientation={'vertical'}
              value={geoFilterMode}
            />

            {geoFilterMode === GeoFilterMode.SAVED_SET && (
              <GeoFilterSet onEditGeoFilterSet={handleEditGeoFilterSet} />
            )}

            {geoFilterMode === GeoFilterMode.CUSTOM_SET && (
              <>
                <Text.DefaultSemibold>{t('Locations')}</Text.DefaultSemibold>
                {filteredTargetings.length === 0
                  ? emptyState
                  : filteredTargetings.map((targeting, index) => {
                      return (
                        <GeoFilter
                          campaign={campaign}
                          key={targeting.id || index}
                          layout={'stacked'}
                          onRemove={handleRemoveFilter}
                          targeting={targeting}
                          targetings={targetings}
                        />
                      );
                    })}
                {filteredTargetings.length > 0 && addFilterButton}
              </>
            )}
          </>
        ) : (
          // Original UI when feature flag is disabled
          <>
            {filteredTargetings.length === 0
              ? emptyState
              : filteredTargetings.map((targeting, index) => {
                  return (
                    <GeoFilter.V1
                      campaign={campaign}
                      key={targeting.id || index}
                      onRemove={handleRemoveFilter}
                      targeting={targeting}
                      targetings={targetings}
                    />
                  );
                })}
            {filteredTargetings.length > 0 && addFilterButton}
          </>
        )}
      </Card.Content>
      {hasSavedGeoFilterSets && geoFilterMode === GeoFilterMode.CUSTOM_SET && (
        <Card.Actions>
          <Button onClick={handleSaveCustomSet} prefix={getIconForAction('save')}>
            {t('Save custom set')}
          </Button>
        </Card.Actions>
      )}

      {/* Modal Components */}
      <SaveCustomSetModal
        onClose={saveCustomSet.close}
        onSave={handleSaveCustomSetSubmit}
        opened={saveCustomSet.isOpen}
      />

      <GeoFilterModal
        context={editCreate.context}
        onClose={editCreate.close}
        opened={editCreate.isOpen}
      />
    </Card>
  );
}

export default observer(Locations);
